import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { Helmet } from 'react-helmet';
import { useTitle } from '../TitleContext';
import Spacer from '../components/Spacer';
import StoreList from './StoreList';
import { useGoogleMapsLoader } from '../GoogleMapsLoaderContext';
import axios from 'axios';

const containerStyle = {
  width: '100%',
  height: '30vh',
};

const defaultCenter = {
  lat: 34.7691,
  lng: 137.3914,
};

const StoreEntry = () => {
  const { defaultTitle } = useTitle();
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [center, setCenter] = useState(defaultCenter);
  const [mapInstance, setMapInstance] = useState(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLoc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(userLoc);
          setCenter(userLoc); // GPS取得できた場合にセンターをユーザー位置に変更
        },
        (error) => {
          console.error('Error getting user location:', error);
        }
      );
    }
  }, []);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response = await axios.get('https://2024110111588835he8k.conohawing.com/mfapirest/wp-json/db-api-manager/v1/app-mikawa-storelist', {
          params: {
            latitude: defaultCenter.lat,
            longitude: defaultCenter.lng,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        });
        setStores(response.data);
      } catch (error) {
        if (error.response) {
          console.error('Error response:', error.response);
        } else if (error.request) {
          console.error('Error request:', error.request);
        } else {
          console.error('Error message:', error.message);
        }
      }
    };

    if (isLoaded) {
      fetchStores();
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded && mapInstance) {
      // 既存のマーカーを削除
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];

      stores.forEach((store) => {
        if (store.latitude && store.longitude) {
          const marker = new window.google.maps.Marker({
            position: { lat: parseFloat(store.latitude), lng: parseFloat(store.longitude) },
            title: store.aws_name,
            map: mapInstance,
          });

          marker.addListener('click', () => {
            setSelectedStoreId(store.id);
          });

          markersRef.current.push(marker);
        }
      });

      if (userLocation) {
        const userMarker = new window.google.maps.Marker({
          position: userLocation,
          title: "Your Location",
          map: mapInstance,
          icon: {
            url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
          },
        });

        markersRef.current.push(userMarker);
      }
    }
    return () => {
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
    };
  }, [isLoaded, stores, mapInstance, userLocation]);

  const calculateDistance = (location1, location2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth's radius in km
    const dLat = toRad(location2.lat - location1.lat);
    const dLng = toRad(location2.lng - location1.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(location1.lat)) *
        Math.cos(toRad(location2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const storesWithDistance = useMemo(() => {
    const withDistance = stores.map((store) => {
      if (userLocation && store.latitude && store.longitude) {
        const distance = calculateDistance(userLocation, {
          lat: parseFloat(store.latitude),
          lng: parseFloat(store.longitude),
        });
        return { ...store, distance };
      }
      return { ...store, distance: null };
    });

    return withDistance.sort((a, b) => {
      if (a.distance === null && b.distance === null) return 0;
      if (a.distance === null) return 1;
      if (b.distance === null) return -1;
      return a.distance - b.distance;
    });
  }, [userLocation, stores]);

  const onLoad = (map) => {
    setMapInstance(map);
  };

  if (loadError) {
    return <Typography variant="h5" align="center">地図の読み込み中にエラーが発生しました。</Typography>;
  }

  return (
    <div>
      <Helmet>
        <title>{`${defaultTitle} - 入店管理`}</title>
      </Helmet>
      <Box sx={{ height: 'calc(100vh - 64px - 56px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ position: 'fixed', top: 64, left: '2vw', right: '2vw', width: '96vw', zIndex: 1 }}>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={10}
              options={{
                disableDefaultUI: true,
                gestureHandling: 'greedy',
              }}
              onLoad={onLoad}
            />
          ) : (
            <Typography variant="h5" align="center">Loading Map...</Typography>
          )}
        </Paper>
        <Spacer height={20} />
        <Box sx={{ mt: '30vh', width: '100%', height: 'calc(100vh - 30vh - 20px - 64px - 56px)', overflowY: 'auto' }}>
          <StoreList stores={storesWithDistance} selectedStoreId={selectedStoreId} />
        </Box>
      </Box>
    </div>
  );
};

export default StoreEntry;
